import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserCog, Plus, Pencil, Trash2, Award, Phone, Camera } from "lucide-react";

export default function Muallems() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const token = localStorage.getItem("token");
  const [muallems, setMuallems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", experience: "", phone: "" });
  const [photo, setPhoto] = useState(null);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const [photoTarget, setPhotoTarget] = useState(null);
  const quickFileRef = useRef(null);

  const load = useCallback(async () => setMuallems((await api.get("/muallems")).data), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditId(null); setForm({ name: "", experience: "", phone: "" }); setPhoto(null); setOpen(true); };
  const openEdit = (m) => { setEditId(m.id); setForm({ name: m.name, experience: m.experience || "", phone: m.phone || "" }); setPhoto(null); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Muallem name is required"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("workspace_id", localStorage.getItem("workspace_id"));
      fd.append("name", form.name);
      fd.append("experience", form.experience);
      fd.append("phone", form.phone);
      if (photo) fd.append("photo", photo);
      if (editId) await api.put(`/muallems/${editId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      else await api.post("/muallems", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(editId ? "Muallem updated" : "Muallem added");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/muallems/${id}`); setMuallems((p) => p.filter((m) => m.id !== id)); toast.success("Muallem removed"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const quickPhoto = async (m, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("workspace_id", localStorage.getItem("workspace_id"));
      fd.append("name", m.name);
      fd.append("experience", m.experience || "");
      fd.append("phone", m.phone || "");
      fd.append("photo", file);
      await api.put(`/muallems/${m.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo updated");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="muallems-page">
      <PageHeader eyebrow="Group Leaders" title="Muallem List"
        subtitle="Your experienced group leaders — assign a muallem to any group date"
        action={isOwner ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-muallem-btn" onClick={openNew} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2"><Plus className="w-4 h-4" /> Add Muallem</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#05261D] border-gold/20" data-testid="muallem-modal">
              <DialogHeader><DialogTitle className="font-serif text-gold flex items-center gap-2"><UserCog className="w-5 h-5" /> {editId ? "Edit Muallem" : "New Muallem"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs text-muted-foreground">Full name</Label><Input data-testid="muallem-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-card/60" placeholder="e.g. Maulana Abdul Karim" /></div>
                <div><Label className="text-xs text-muted-foreground">Mobile</Label><Input data-testid="muallem-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-card/60" placeholder="+91…" /></div>
                <div><Label className="text-xs text-muted-foreground">Experience</Label><Textarea data-testid="muallem-exp-input" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} className="mt-1 bg-card/60 min-h-[70px]" placeholder="e.g. 12 years leading Umrah & Hajj groups, fluent Arabic & Bengali" /></div>
                <div>
                  <Label className="text-xs text-muted-foreground">Photo</Label>
                  <input ref={fileRef} type="file" accept="image/*" data-testid="muallem-photo-input" onChange={(e) => setPhoto(e.target.files?.[0] || null)} className="mt-1 block w-full text-xs text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-gold file:text-[#0a2e22] file:font-semibold" />
                </div>
              </div>
              <DialogFooter>
                <Button data-testid="save-muallem-btn" onClick={save} disabled={saving} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold">{saving ? "Saving…" : "Save Muallem"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : null}
      />
      <div className="p-4 sm:p-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {muallems.length === 0 && <p className="text-sm text-muted-foreground col-span-full text-center py-12">No muallems yet. {isOwner ? "Add your first group leader." : "The admin will add group leaders here."}</p>}
        {muallems.map((m) => (
          <Card key={m.id} data-testid={`muallem-card-${m.id}`} className="p-4 bg-card/40 border-border/60">
            <div className="flex items-start gap-3">
              <div className="relative flex-shrink-0">
                {m.photo_path ? (
                  <img src={`${API}/muallems/${m.id}/photo?auth=${token}`} alt={m.name} className="w-16 h-16 rounded-full object-cover ring-2 ring-gold/40" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-primary/50 border border-gold/30 flex items-center justify-center text-xl font-serif text-gold">{m.name[0]}</div>
                )}
                {isOwner && (
                  <button data-testid={`muallem-photo-upload-${m.id}`} title="Upload / change photo"
                    onClick={() => { setPhotoTarget(m); quickFileRef.current?.click(); }}
                    className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-gold text-[#0a2e22] flex items-center justify-center shadow hover:bg-gold/90 transition-colors">
                    <Camera className="w-3 h-3" />
                  </button>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-serif font-semibold text-white">{m.name}</p>
                {m.phone && <p className="text-[11px] text-muted-foreground font-mono flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" />{m.phone}</p>}
                {m.experience && <p className="text-xs text-muted-foreground mt-1.5 flex items-start gap-1"><Award className="w-3 h-3 text-gold mt-0.5 flex-shrink-0" />{m.experience}</p>}
              </div>
            </div>
            {isOwner && (
              <div className="flex gap-2 mt-3 justify-end">
                <Button size="icon" variant="ghost" data-testid={`muallem-edit-${m.id}`} onClick={() => openEdit(m)} className="h-8 w-8 text-gold hover:bg-gold/15"><Pencil className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="ghost" data-testid={`muallem-delete-${m.id}`} onClick={() => remove(m.id)} className="h-8 w-8 text-destructive hover:bg-destructive/15"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            )}
          </Card>
        ))}
      </div>
      <input ref={quickFileRef} type="file" accept="image/*" className="hidden" data-testid="muallem-quick-photo-input"
        onChange={(e) => { const f = e.target.files?.[0]; if (f && photoTarget) quickPhoto(photoTarget, f); e.target.value = ""; }} />
    </div>
  );
}
